/**
 * Despacho — vista principal.
 *
 *   - tab=pendientes  → comprobantes pendientes de trazar (datos REALES del ERP
 *                       ACSIS vía backend: GET /api/despacho/pendientes).
 *   - Click en un card → detalle del comprobante (header + items + partidas con
 *     N°serie/lote/vencimiento) traído desde ACSIS.
 *   - tab=completadas → tabla de operaciones del usuario en GLN activo (mock).
 *   - tab=reingresos  → unidades con el egreso anulado "en tránsito" que
 *                       vuelven: se confirma su reingreso físico.
 *
 * Nota: el flujo de pickeo/trazado/cumplir-tarea se reconecta como próximo paso
 * (POST /editar/cumplir-tarea cuando el operador termina de escanear).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { PendientesTareasList } from './PendientesTareasList';
import { ComprobanteDetalleView } from './ComprobanteDetalleView';
import { CompletadasTrazadasList } from './CompletadasTrazadasList';
import { ReingresosPendientesList } from './ReingresosPendientesList';
import { useSessionStore } from '@/stores/session';
import type { TareaPendiente, TareaTipo } from '@/api/contracts/tareas';

type Tab = 'pendientes' | 'completadas' | 'reingresos';

// Solapa Reingresos oculta hasta analizar el flujo con el cliente. El
// componente y el back quedan intactos; para volver a mostrarla, poner en true.
const MOSTRAR_REINGRESOS = false;

/**
 * Despacho a abrir al entrar, pasado por el state de la navegación (ej. el
 * "Ir al despacho" del modal de Trazabilidad). El detalle no tiene URL propia.
 */
interface AbrirDespacho {
  referencia: string;
  tipo: Exclude<TareaTipo, 'otro'>;
  numeroRemito: string | null;
  /** Traza hecha → se abre como Completada (congelada); si no, como Pendiente. */
  trazaHecha: boolean;
}

function leerAbrirDespacho(state: unknown): AbrirDespacho | null {
  const a = (state as { abrirDespacho?: Partial<AbrirDespacho> } | null)?.abrirDespacho;
  if (!a || typeof a.referencia !== 'string' || !a.tipo) return null;
  return {
    referencia: a.referencia,
    tipo: a.tipo,
    numeroRemito: a.numeroRemito ?? null,
    trazaHecha: Boolean(a.trazaHecha),
  };
}

/** Tarea mínima para abrir el detalle (como `completadaToTarea` en Completadas).
 *  La sigla sale de la referencia (`1-1-RES-42378` → `RES`); el resto de los
 *  datos los trae el propio detalle. */
function abrirDespachoToTarea(a: AbrirDespacho): TareaPendiente {
  const sigla = a.referencia.split('-')[2] ?? '—';
  const destruccion = sigla === 'DES';
  return {
    id: `abrir-${a.referencia}`,
    tipo: a.tipo,
    sigla,
    tipoLabel: destruccion ? 'Destrucción' : 'Salida',
    movimiento: destruccion ? 'destruccion' : 'salida',
    claseComprobante: null,
    referencia: a.referencia,
    numeroRemito: a.numeroRemito,
    entidad: null,
    asunto: null,
    descripcion: null,
    prioridad: null,
    solicitante: null,
    nombreSolicitante: null,
    responsable: null,
    nombreResponsable: null,
    fechaCreacion: null,
    fechaVencimiento: null,
    fechaAsignacion: null,
    traza: null,
  };
}

export function DespachoPage() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [abrirInicial] = useState(() => leerAbrirDespacho(location.state));
  // Deep-link desde el dashboard: ?tab=completadas&cierreErp=error abre
  // directamente la solapa Completadas (el filtro lo lee CompletadasTrazadasList).
  const [tab, setTab] = useState<Tab>(
    abrirInicial
      ? abrirInicial.trazaHecha
        ? 'completadas'
        : 'pendientes'
      : searchParams.get('tab') === 'completadas'
        ? 'completadas'
        : 'pendientes',
  );
  const [tareaActiva, setTareaActiva] = useState<TareaPendiente | null>(() =>
    abrirInicial ? abrirDespachoToTarea(abrirInicial) : null,
  );

  // El state de la navegación se consume una sola vez: se limpia para que un
  // refresh o volver con el navegador no reabra el mismo despacho.
  useEffect(() => {
    if (abrirInicial) {
      void navigate({ search: location.search }, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);
  // Filtro por tipo (sigla) del listado de pendientes. Vive acá (no en la lista)
  // para que persista al entrar a un despacho y volver al listado.
  const [siglaFiltro, setSiglaFiltro] = useState<string | null>(null);

  // Si el usuario cambia de GLN desde el switcher mientras está viendo el
  // detalle de un remito, ese remito pertenece a la GLN anterior — lo
  // correcto es volver al listado para no inducir confusión (¿este remito es
  // de la GLN nueva o de la vieja?). El listado se refresca solo con el
  // nuevo glnId.
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const ubicacionPrevia = useRef(ubicacionActivaId);
  useEffect(() => {
    if (ubicacionPrevia.current !== ubicacionActivaId) {
      ubicacionPrevia.current = ubicacionActivaId;
      setTareaActiva(null);
      setSiglaFiltro(null); // el filtro de tipo no aplica al cambiar de GLN
    }
  }, [ubicacionActivaId]);

  // Cuando volvemos del detalle al listado, invalidamos pendientes y
  // completadas. Sin esto, el listado se queda con el caché vieja (60s de
  // refetchInterval) y muestra "Trazando..." aunque el poller del backend ya
  // haya marcado el job como success — el usuario ve un estado obsoleto.
  const tareaPrevia = useRef(tareaActiva);
  useEffect(() => {
    if (tareaPrevia.current && !tareaActiva) {
      void qc.invalidateQueries({ queryKey: ['tareas-pendientes'] });
      void qc.invalidateQueries({ queryKey: ['despacho-completadas'] });
    }
    tareaPrevia.current = tareaActiva;
  }, [tareaActiva, qc]);

  // En modo detalle ocultamos el header de tabs para no distraer.
  if (tareaActiva) {
    return (
      <ComprobanteDetalleView
        tarea={tareaActiva}
        onVolver={() => setTareaActiva(null)}
        esCompletada={tab === 'completadas'}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">Despacho</CardTitle>
          <p className="text-xs text-gray-600">
            Trazá los comprobantes pendientes generados por el ERP:
            remitos de venta, transferencias y movimientos de destrucción.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 border-b border-gray-200">
            <TabButton active={tab === 'pendientes'} onClick={() => setTab('pendientes')}>
              Pendientes
            </TabButton>
            <TabButton active={tab === 'completadas'} onClick={() => setTab('completadas')}>
              Completadas
            </TabButton>
            {MOSTRAR_REINGRESOS && (
              <TabButton active={tab === 'reingresos'} onClick={() => setTab('reingresos')}>
                Reingresos
              </TabButton>
            )}
          </div>
        </CardContent>
      </Card>

      {tab === 'pendientes' ? (
        <PendientesTareasList
          onAbrirTarea={setTareaActiva}
          siglaFiltro={siglaFiltro}
          onSiglaFiltroChange={setSiglaFiltro}
        />
      ) : tab === 'completadas' ? (
        <CompletadasTrazadasList onAbrirTarea={setTareaActiva} />
      ) : (
        <ReingresosPendientesList />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm transition',
        active
          ? 'border-b-2 border-everex-naranja font-semibold text-everex-azul'
          : 'text-gray-600 hover:text-everex-azul',
      )}
    >
      {children}
    </button>
  );
}
